import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { gsrApi, type GsrType } from '../features/gsr/api.ts';
import { gsrStatusLabel, gsrStatusPill } from '../features/gsr/status.ts';
import { PurchaseRequestPanel } from '../features/gsr/PurchaseRequestPanel.tsx';
import { PrPrepareForm } from '../features/gsr/PrPrepareForm.tsx';
import { RequestPanel } from '../features/gsr/RequestPanel.tsx';
import { api } from '../lib/api.ts';
import { downloadPdf } from '../features/documents/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { canProcure, canStock } from '../lib/roles.ts';
import { t } from '../lib/strings.ts';
import { formatDate } from '../lib/format.ts';

const TYPE_LABELS: Record<GsrType, string> = {
  GOODS: t.gsrDetail.typeGoods,
  SERVICE: t.gsrDetail.typeService,
};

type TabId = 'requisition' | 'purchaseRequest' | 'quotation';

export function GsrDetail(): JSX.Element {
  const { gsrId } = useParams({ from: '/gsr/$gsrId' });
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const mayProcure = canProcure(role);
  const mayStock = canStock(role);

  const [sending, setSending] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [generatingRfq, setGeneratingRfq] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId | null>(null);

  const { data: gsr, isLoading } = useQuery({
    queryKey: ['gsr', gsrId],
    queryFn: () => gsrApi.get(gsrId),
    refetchOnWindowFocus: true,
  });

  // Compute unlock states
  const isGoods = gsr?.type === 'GOODS';
  const gsrApproved = gsr?.status === 'APPROVED' || gsr?.status === 'RFQ_GENERATED';
  const tab2Enabled = isGoods && gsrApproved;
  const tab3Enabled = gsrApproved && (!isGoods || gsr?.purchaseRequest?.status === 'APPROVED');

  // Default to furthest unlocked tab (only once, when gsr first loads)
  const resolvedTab: TabId = useMemo(() => {
    if (activeTab !== null) return activeTab;
    if (!gsr) return 'requisition';
    if (tab3Enabled) return 'quotation';
    if (tab2Enabled) return 'purchaseRequest';
    return 'requisition';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gsr?.id]);

  const currentTab = activeTab ?? resolvedTab;

  const handleSendToSign = async () => {
    setError(null);
    setSending(true);
    try {
      const result = await gsrApi.sendForSigning(gsrId);
      const h = await api.post<{ url: string }>('/wapsign/handoff', { redirectPath: result.editorPath });
      await qc.invalidateQueries({ queryKey: ['gsr', gsrId] });
      window.location.href = h.data.url;
    } catch {
      setError(t.gsrDetail.errorSend);
    } finally {
      setSending(false);
    }
  };

  const handleContinueSigning = async () => {
    if (!gsr?.signDocId) {
      setError(t.gsrDetail.errorSend);
      return;
    }
    setError(null);
    setSending(true);
    try {
      const h = await api.post<{ url: string }>('/wapsign/handoff', {
        redirectPath: `/documents/${gsr.signDocId}/edit`,
      });
      window.location.href = h.data.url;
    } catch {
      setError(t.gsrDetail.errorSend);
    } finally {
      setSending(false);
    }
  };

  const handleWithdraw = async () => {
    setError(null);
    setWithdrawing(true);
    try {
      await gsrApi.withdrawSigning(gsrId);
      await qc.invalidateQueries({ queryKey: ['gsr', gsrId] });
    } catch {
      setError(t.gsrDetail.errorWithdraw);
    } finally {
      setWithdrawing(false);
    }
  };


  const handleGenerateRfq = async () => {
    setError(null);
    setGeneratingRfq(true);
    try {
      await gsrApi.generateRfq(gsrId);
      await qc.invalidateQueries({ queryKey: ['gsr', gsrId] });
      setActiveTab('quotation');
    } catch {
      setError(t.gsrDetail.errorGenerateRfq);
    } finally {
      setGeneratingRfq(false);
    }
  };

  const handleDownloadPdf = () => {
    void downloadPdf(`/documents/gsr/${gsrId}`, `gsr-${gsrId}.pdf`);
  };

  if (isLoading) {
    return (
      <div className="page">
        <p className="status-label text-muted-foreground" role="status">{t.gsrDetail.loading}</p>
      </div>
    );
  }

  if (!gsr) {
    return (
      <div className="page">
        <p className="text-sm text-destructive">{t.gsrDetail.notFound}</p>
      </div>
    );
  }

  const items = gsr.items ?? [];

  // Tab definitions — Tab 2 hidden for SERVICE
  type TabDef = { id: TabId; label: string; enabled: boolean };
  const tabs: TabDef[] = [
    { id: 'requisition', label: t.gsrDetail.tabs.requisition, enabled: true },
    ...(isGoods ? [{ id: 'purchaseRequest' as TabId, label: t.gsrDetail.tabs.purchaseRequest, enabled: tab2Enabled }] : []),
    { id: 'quotation', label: t.gsrDetail.tabs.quotation, enabled: tab3Enabled },
  ];

  return (
    <div className="page space-y-6">
      {/* Page header — back link, GSR number as the prominent identifier, type +
          status pills, and the created date as mono meta. Mirrors WapMeet's
          MeetingDetail masthead so this reads as the same product family. */}
      <header className="space-y-4">
        <Link to="/gsr" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <Icon name="arrow_forward" size={15} />
          {t.gsrDetail.ariaBack}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1.5">
            <h1 className="font-serif text-2xl sm:text-3xl tracking-tight leading-tight">{gsr.title || gsr.gsrNumber}</h1>
            {gsr.title && <p className="mono-meta text-muted-foreground" dir="ltr">{gsr.gsrNumber}</p>}
            <div className="flex flex-wrap items-center gap-2">
              <span className={gsrStatusPill(gsr)}>{gsrStatusLabel(gsr)}</span>
              <span className="pill pill-neutral">{TYPE_LABELS[gsr.type]}</span>
            </div>
          </div>
          <div className="shrink-0 space-y-1 text-end">
            {gsr.section && (
              <p className="text-sm text-muted-foreground">{t.gsrDetail.section}: {gsr.section}</p>
            )}
            <p className="mono-meta text-muted-foreground">
              {t.gsrDetail.createdAt} <span className="num">{formatDate(gsr.createdAt)}</span>
            </p>
          </div>
        </div>
      </header>

      {/* Tab bar — underline-active style, consistent with the section-heading
          hairline vocabulary used elsewhere in the app. Wraps instead of
          horizontally scrolling — only 3 short tabs, no need for a scrollbar
          on desktop; wrapping keeps it readable on narrow/mobile widths. */}
      <div className="flex flex-wrap gap-1 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            disabled={!tab.enabled}
            onClick={() => tab.enabled && setActiveTab(tab.id)}
            title={!tab.enabled ? t.gsrDetail.tabs.lockHint : undefined}
            className={[
              'flex items-center gap-1.5 whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              currentTab === tab.id
                ? 'border-primary text-foreground'
                : tab.enabled
                  ? 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  : 'border-transparent text-muted-foreground/50 cursor-not-allowed',
            ].join(' ')}
            aria-current={currentTab === tab.id ? 'page' : undefined}
          >
            {!tab.enabled && <Icon name="lock" size={12} className="shrink-0" />}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {/* ── Tab 1: Requisition ─────────────────────────────────────────────── */}
        {currentTab === 'requisition' && (
          <div className="space-y-6">
            {/* Identity line — number · status · prepared date (mirrors the PR tab) */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="mono-meta text-muted-foreground num" dir="ltr">{gsr.gsrNumber}</span>
              <span className={gsrStatusPill(gsr)}>{gsrStatusLabel(gsr)}</span>
              <span className="text-sm text-muted-foreground">
                {t.gsrDetail.createdAt} <span className="num">{formatDate(gsr.createdAt)}</span>
              </span>
            </div>

            {/* Action row — primary (send/continue signing) + secondary (PDF, withdraw) */}
            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
              {mayProcure && gsr.status === 'DRAFT' && (
                <button
                  onClick={() => void handleSendToSign()}
                  disabled={sending}
                  className="btn-primary gap-1.5"
                >
                  <Icon name="send" size={14} />
                  {sending ? t.gsrDetail.sending : t.gsrDetail.sendToSign}
                </button>
              )}

              {mayProcure && gsr.status === 'PENDING_SIGNATURE' && (
                <>
                  <button
                    onClick={() => void handleContinueSigning()}
                    disabled={sending}
                    className="btn-primary gap-1.5"
                  >
                    <Icon name="send" size={14} />
                    {sending ? t.gsrDetail.sending : t.gsrDetail.continueSigning}
                  </button>
                  <button
                    onClick={() => void handleWithdraw()}
                    disabled={withdrawing}
                    className="btn-secondary gap-1.5"
                  >
                    <Icon name="restart_alt" size={14} />
                    {withdrawing ? t.gsrDetail.withdrawing : t.gsrDetail.withdraw}
                  </button>
                </>
              )}

              <button onClick={handleDownloadPdf} className="btn-secondary gap-1.5">
                <Icon name="download" size={14} />
                {t.gsrDetail.downloadPdf}
              </button>

              {(gsr.status === 'APPROVED' || gsr.status === 'RFQ_GENERATED') && (
                <span className="pill pill-success">{t.gsrDetail.approvedBadge}</span>
              )}
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            {/* Items table */}
            <section className="space-y-2">
              <h2 className="section-heading">
                <span>{t.gsrDetail.itemsHeading} (<span className="num">{items.length}</span>)</span>
              </h2>

              {/* Mobile: stacked cards */}
              <ul className="space-y-2 md:hidden">
                {items.map((item, idx) => (
                  <li key={item.id} className="card-paper p-3.5">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-medium leading-snug">{item.particulars}</p>
                      <span className="mono-meta text-muted-foreground shrink-0 num">#{idx + 1}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <span><span className="tabular text-foreground num">{item.qtyRequested}</span> {item.unit}</span>
                      {item.requiredDate && <span className="num">{formatDate(item.requiredDate)}</span>}
                      {item.remarks && <span>{item.remarks}</span>}
                    </div>
                  </li>
                ))}
              </ul>

              {/* Desktop: table */}
              <div className="card-paper hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="p-3 text-start status-label text-muted-foreground">{t.gsrDetail.colNum}</th>
                      <th className="p-3 text-start status-label text-muted-foreground">{t.gsrDetail.colParticulars}</th>
                      <th className="p-3 text-end status-label text-muted-foreground">{t.gsrDetail.colQty}</th>
                      <th className="p-3 text-start status-label text-muted-foreground">{t.gsrDetail.colUnit}</th>
                      <th className="p-3 text-start status-label text-muted-foreground">{t.gsrDetail.colRequiredDate}</th>
                      <th className="p-3 text-start status-label text-muted-foreground">{t.gsrDetail.colRemarks}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={item.id} className="border-t border-border hover:bg-muted/40 transition-colors">
                        <td className="p-3 mono-meta text-muted-foreground num">{idx + 1}</td>
                        <td className="p-3 font-medium">{item.particulars}</td>
                        <td className="p-3 text-end tabular num">{item.qtyRequested}</td>
                        <td className="p-3 text-muted-foreground">{item.unit}</td>
                        <td className="p-3 text-muted-foreground num">{item.requiredDate ? formatDate(item.requiredDate) : '—'}</td>
                        <td className="p-3 text-muted-foreground">{item.remarks ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}

        {/* ── Tab 2: Purchase Request (GOODS only) ────────────────────────────── */}
        {currentTab === 'purchaseRequest' && isGoods && (
          <div>
            {gsr.purchaseRequest ? (
              <PurchaseRequestPanel prId={gsr.purchaseRequest.id} gsrId={gsrId} />
            ) : mayStock ? (
              <PrPrepareForm
                gsrId={gsrId}
                items={items}
                onCreated={() => {
                  void qc.invalidateQueries({ queryKey: ['gsr', gsrId] });
                  setActiveTab('purchaseRequest');
                }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">{t.gsrDetail.noPrYet}</p>
            )}
          </div>
        )}

        {/* ── Tab 3: Quotation ─────────────────────────────────────────────────── */}
        {currentTab === 'quotation' && (
          <div>
            {gsr.request ? (
              <RequestPanel requestId={gsr.request.id} />
            ) : (
              <div className="card-paper space-y-4 p-5">
                <p className="text-sm text-muted-foreground">{t.gsrDetail.noRfqYet}</p>
                {mayProcure && (
                  <button
                    onClick={() => void handleGenerateRfq()}
                    disabled={generatingRfq}
                    className="btn-primary btn-sm"
                  >
                    {generatingRfq ? t.gsrDetail.generatingRfq : t.gsrDetail.generateRfqAction}
                  </button>
                )}
                {error && <p className="text-sm text-destructive">{error}</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
